import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/button';
import ErrorWithSupport from '@/components/support/ErrorWithSupport';
import { FEATURES } from '@/constants';
import { useDucatClient } from '@/context/DucatClientCtx';
import { useAppStore, useNetwork } from '@/stores/useAppStore';
import { useNotificationStore } from '@/stores/useNotificationStore';

import { getIsQuantaRoutes } from '../utils';

import BitcoinBanner from './bitcoin-banner';
import DropdownNav from './dropdown-nav';
import HeaderTitle from './HeaderTitle';
import Icons from './Icons';
import Label from './label';
import WalletConnect from './wallet-connect';

const SettingsDrawer = dynamic(() => import('./settings/SettingsDrawer'));
const NotificationCenter = dynamic(() => import('./NotificationCenter'));

const BorrowQuantaChip = dynamic(() => import('./BorrowQuantaChip'));

const TESTNET_GUIDE_URL = 'https://ducatstable.notion.site/How-To-Use-Ducat-Protocol-on-Testnet-137941be6f5e80fbb3acc7e15a53c240';

const ERROR_MESSAGE_KEYS = {
  networkMismatchWithGuide: 'walletMismatchWithGuide',
  networkMismatch: 'walletMismatch',
} as const;

type HeaderErrorKey = keyof typeof ERROR_MESSAGE_KEYS;

const testnetGuideLink = (chunks: React.ReactNode) => (
  <Link href={TESTNET_GUIDE_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-85">
    {chunks}
  </Link>
);

const Header = () => {
  const t = useTranslations('layout.header');
  const tSettings = useTranslations('settings');
  const pathname = usePathname();
  const selectedNetwork = useNetwork();
  const { wallet, connectedNetwork, client } = useDucatClient();
  const walletErrorMessage = useAppStore(({ walletErrorMessage }) => walletErrorMessage);
  const { notifications, getUnviewedCount, hasPendingVaultAction, hasSwapUserActionRequired, markAllAsViewed, clearAll } =
    useNotificationStore(
      useShallow(({ notifications, getUnviewedCount, hasPendingVaultAction, hasSwapUserActionRequired, markAllAsViewed, clearAll }) => ({
        notifications,
        getUnviewedCount,
        hasPendingVaultAction,
        hasSwapUserActionRequired,
        markAllAsViewed,
        clearAll,
      })),
    );

  const [error, setError] = useState<HeaderErrorKey | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const openSettings = () => setIsSettingsOpen(true);
  const closeSettings = () => setIsSettingsOpen(false);

  const isQuantaRoutes = getIsQuantaRoutes(pathname);

  const renderErrorMessage = () => {
    if (walletErrorMessage) {
      return t.rich('walletError', { message: walletErrorMessage, link: testnetGuideLink });
    }

    return error ? t.rich(ERROR_MESSAGE_KEYS[error], { link: testnetGuideLink }) : null;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO: check array dependencies>
  useEffect(() => {
    if (wallet === null && pathname !== '/overview') {
      // setError("Please connect your wallet");
    } else if (connectedNetwork === 'main' && !['main', 'alpha-mainnet'].includes(selectedNetwork as string)) {
      setError('networkMismatchWithGuide');
      client.disconnect();
    } else {
      setError(null);
    }
  }, [selectedNetwork, connectedNetwork, wallet]); // FIXME: should add pathname to deps array or remove pathname !== '/overview' or combine if & else if

  return (
    <>
      <header className="fixed top-0 left-0 lg:left-[264px] w-full lg:w-[calc(100%-264px)] h-[74px] lg:h-[94px] flex items-center justify-between px-6 lg:px-6 1320:px-8 py-4 lg:py-6 border-b lg:border-b-2 border-border-primary gap-2 1320:gap-4 bg-gray-700 z-40">
        <HeaderTitle pathname={pathname} />
        <div className="lg:hidden flex min-w-0 flex-1 gap-2 items-center">
          <Label pathname={pathname} />
          <DropdownNav pathname={pathname} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!isQuantaRoutes && <BitcoinBanner />}
          {wallet && <BorrowQuantaChip />}
          <WalletConnect />
          <Button
            ref={settingsButtonRef}
            variant="secondary"
            className="h-[30px]! w-[30px]! lg:h-10! lg:w-10!"
            onClick={openSettings}
            aria-label={tSettings('title')}
          >
            <Icons.Settings />
          </Button>
          {FEATURES.notificationCenter && (
            <NotificationCenter
              hasPendingVaultAction={hasPendingVaultAction()}
              hasUserActionRequired={hasSwapUserActionRequired()}
              count={getUnviewedCount()}
              notifications={notifications}
              markAllAsViewed={markAllAsViewed}
              clearAll={clearAll}
            />
          )}
        </div>
        {(error || walletErrorMessage) && (
          <div className="absolute top-[74px] lg:top-[94px] left-0 flex items-center justify-center w-full p-3 bg-red-100">
            <ErrorWithSupport className="text-text-primary font-medium text-lg whitespace-pre-line text-center">
              {renderErrorMessage()}
            </ErrorWithSupport>
          </div>
        )}
      </header>
      {isSettingsOpen && (
        <SettingsDrawer
          isOpen={isSettingsOpen}
          closeSettings={closeSettings}
          onCloseAutoFocus={(event) => {
            const button = settingsButtonRef.current;
            if (button?.isConnected) {
              event.preventDefault();
              button.focus();
            }
          }}
        />
      )}
    </>
  );
};

export default Header;
